import {
  call,
  put,
  takeEvery,
  takeLatest,
  all,
  takeLeading,
  select,
} from 'redux-saga/effects';
import { getFirebase } from 'react-redux-firebase';
import { getResourcesIndex } from 'containers/Resources/utils';
import _get from 'lodash/get';
import AlgoliaClient from 'utils/algoliaService';
import { post } from 'utils/api';
import client from 'utils/contentfulService';
import Config from 'utils/getEnvConfig';
import GqlClient from 'utils/graphqlService';
import MailChimpService from 'utils/mailchimpService';
import { isBot } from 'utils/stringUtils';
import GetClientDetails from './services/getClientDetails.gql';
import GetThemesQuery from './services/getThemes.gql';
import GetSiteCopy from './services/getSiteCopy.gql';

import {
  GET_CLIENT_DETAILS,
  SEND_MAIL,
  GET_SITE_COPY,
  GET_PROGRAMS_BY_TOPIC,
  GET_THEMES,
  GET_RESOURCES_COUNT_BY_TOPIC,
  GET_CLIENT_RESOURCES,
  UPDATE_BRAND,
} from './constants';
import {
  processing,
  getClientDetailsSuccess,
  sendMailSuccess,
  getSiteCopySuccess,
  getProgramsByTopicSuccess,
  getThemesSuccess,
  getResourcesCountByTopicSuccess,
  getClientResourcesSuccess,
  clientDetailsFetching,
} from './actions';
import { makeSelectClientDetails } from './selectors';

function* getClientDetailsSaga({ payload }) {
  try {
    yield put(clientDetailsFetching(true));
    const { data } = yield call(() =>
      GqlClient.query({
        query: GetClientDetails,
        variables: {
          shortName: payload.toLowerCase(),
        },
      }),
    );
    const items = data ? _get(data, 'clientCollection.items.0') : [];

    yield put(getClientDetailsSuccess(items));
  } catch (error) {
    throw new Error(error);
  } finally {
    yield put(clientDetailsFetching(false));
  }
}

function* sendMailSaga({ payload }) {
  try {
    const { type = 'feedback', ...data } = payload;
    const endpoint =
      type === 'contact'
        ? Config.LAMBDA.MANDRILL_CONTACT
        : Config.LAMBDA.MANDRILL_FEEDBACK;
    yield put(processing(true));
    yield call(
      post,
      endpoint.URL,
      {
        ...data,
      },
      {
        headers: {
          'x-api-key': endpoint.API_KEY,
        },
      },
    );
    yield put(sendMailSuccess());
  } catch (error) {
    // handle error here
  } finally {
    yield put(processing(false));
  }
}

function* getSiteCopySaga() {
  try {
    yield put(processing(true));
    const { data } = yield call(() =>
      GqlClient.query({
        query: GetSiteCopy,
      }),
    );
    const items = data ? _get(data, 'siteCopyCollection.items') : [];
    yield put(getSiteCopySuccess(items));
  } finally {
    yield put(processing(false));
  }
}

function* getProgramsByTopic({ payload }) {
  try {
    yield put(processing(true));
    const ctfPayload = {
      content_type: 'onlineProgram',
      'fields.reviewStatus': 'Accepted',
      ...payload,
    };
    const response = yield call(() => client.getEntries(ctfPayload));
    yield put(getProgramsByTopicSuccess(response));
  } catch (error) {
    throw new Error(error);
  }
}

function* getThemesSaga({ payload }) {
  const {
    data: {
      themeCollection: { items },
    },
  } = yield call(() =>
    GqlClient.query({
      query: GetThemesQuery,
      variables: {
        title: payload,
      },
      skip: !payload,
    }),
  );
  yield put(getThemesSuccess(items));
}

function* getResourcesByTopicSaga({ payload }) {
  try {
    if (isBot()) throw new Error('Do not call Algolia for Bot requests');
    const clientDetails = yield select(makeSelectClientDetails());
    const resourcesWeightings = _get(
      clientDetails,
      'metadata.algoliaResourcesWeight',
    );

    const algoliaIndex = AlgoliaClient.initIndex(
      getResourcesIndex(resourcesWeightings),
    );
    const topicFilters = `AND allTopics.title:'${payload}'`;
    const response = yield call(() =>
      algoliaIndex.searchForFacetValues('type', '', {
        maxFacetHits: 100,
        filters: `reviewStatus:'Accepted' AND NOT type:'Topics' AND NOT type:'Assessments' AND NOT type:'People' ${topicFilters}`,
      }),
    );
    yield put(getResourcesCountByTopicSuccess(response.facetHits));
  } catch (error) {
    throw new Error(error);
  }
}

function* getClientResourcesSaga({ payload }) {
  const { finalSlug, isLandingPage } = payload;
  try {
    yield put(processing(true));
    let ctfPayload = {
      content_type: 'client',
      'fields.shortName': finalSlug,
      select: 'fields.clientResources',
      include: 2,
    };
    if (isLandingPage) {
      ctfPayload = {
        content_type: 'landing',
        'fields.slug': finalSlug,
        select: 'fields.clientResources',
        include: 1,
      };
    }
    const response = yield call(() => client.getEntries(ctfPayload));
    yield put(
      getClientResourcesSuccess(
        _get(response, 'items.0.fields.clientResources'),
      ),
    );
  } catch (error) {
    throw new Error(error);
  }
}

function* updateBrandSaga({ payload }) {
  const { brand, email } = payload;
  yield all([
    getFirebase().updateProfile({ brand }),
    call(() => MailChimpService.patchSubscriptionBrand(email)),
  ]);
}

// Individual exports for testing
export default function* defaultSaga() {
  yield takeLatest(GET_CLIENT_DETAILS, getClientDetailsSaga);
  yield takeEvery(SEND_MAIL, sendMailSaga);
  yield takeLatest(GET_SITE_COPY, getSiteCopySaga);
  yield takeLatest(GET_PROGRAMS_BY_TOPIC, getProgramsByTopic);
  yield takeLatest(GET_THEMES, getThemesSaga);
  yield takeLatest(GET_RESOURCES_COUNT_BY_TOPIC, getResourcesByTopicSaga);
  yield takeLatest(GET_CLIENT_RESOURCES, getClientResourcesSaga);
  yield takeLeading(UPDATE_BRAND, updateBrandSaga);
}
